import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import {
  LaneBerthReservation,
  LaneManualAssignment,
  ShipmentOrderLaneDetail,
} from 'src/entities/ShipmentOrderLane';
import { useQueryCompany } from 'src/hooks/useQueryCompany';
import { useMutationShipmentOrder } from 'src/hooks/useQueryShipmentOrders';
import {
  SHIPMENT_ORDER_LANE_DETAIL_QUERY_KEY,
  useMutationShipmentOrderLane,
  UpdateShipmentOrderLaneRequest,
} from 'src/hooks/useShipmentOrderLanes';
import {
  buildShipmentDetailFormDataForLaneSave,
  createLaneBasicInfoDraftFromDetail,
  LaneBasicInfoDraft,
  validateLaneBasicInfoDraft,
} from 'src/utils/buildLaneBasicInfoShipmentPatch';
import {
  LOADING_UNLOADING_DATE_RANGE_ERROR_MESSAGE,
} from 'src/utils/shipmentOrder.util';

export type EditableAssignment = LaneManualAssignment & {
  tempId: string;
  deliveryCount?: number;
  isAutoAssignment?: boolean;
  legRunIds?: number[];
};

export type EditableBerthReservation = LaneBerthReservation & {
  tempId: string;
};

type UseLaneDetailEditReturn = {
  isEditing: boolean;
  isSaving: boolean;

  requiredTruckCount: number | null;
  berthReservations: EditableBerthReservation[];
  manualAssignments: EditableAssignment[];
  modified: boolean;

  basicInfoDraft: LaneBasicInfoDraft | null;
  setBasicInfoDraft: React.Dispatch<React.SetStateAction<LaneBasicInfoDraft | null>>;
  markBasicInfoDirty: () => void;

  startEditing: () => void;
  cancelEditing: () => void;
  saveChanges: () => Promise<void>;

  setRequiredTruckCount: (value: number | null) => void;
  updateBerthReservation: (tempId: string, field: keyof Omit<LaneBerthReservation, 'id'>, value: string | null) => void;
  addBerthReservation: (kind: 'loading' | 'unloading') => void;
  removeBerthReservation: (tempId: string) => void;
  updateManualAssignment: (tempId: string, field: keyof Omit<LaneManualAssignment, 'id'>, value: string | number | null) => void;
  addManualAssignment: () => void;
  removeManualAssignment: (tempId: string) => void;
};

/**
 * レーン詳細編集用フック
 */
export const useLaneDetailEdit = (
  detail: ShipmentOrderLaneDetail | null | undefined,
  companyId: number,
): UseLaneDetailEditReturn => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const updateLaneMutation = useMutationShipmentOrderLane();
  const { updateShipmentOrder } = useMutationShipmentOrder();
  const { data: companyData } = useQueryCompany(companyId);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [requiredTruckCount, setRequiredTruckCount] = useState<number | null>(null);
  const [berthReservations, setBerthReservations] = useState<EditableBerthReservation[]>([]);
  const [manualAssignments, setManualAssignments] = useState<EditableAssignment[]>([]);
  const [modified, setModified] = useState(false);
  const [basicInfoDraft, setBasicInfoDraft] = useState<LaneBasicInfoDraft | null>(null);
  const [basicInfoDirty, setBasicInfoDirty] = useState(false);

  const companyCustomNames = companyData?.custom_input_fields ?? [];

  const resetBasicInfoDraftFromDetail = useCallback(() => {
    if (!detail) {
      setBasicInfoDraft(null);
      return;
    }
    setBasicInfoDraft(createLaneBasicInfoDraftFromDetail(detail, companyCustomNames));
  }, [detail, companyCustomNames]);

  useEffect(() => {
    if (!detail || !isEditing) return;

    if (requiredTruckCount > 0 && detail.requiredTruckCount !== requiredTruckCount) {
      setModified(true);
    }
  }, [isEditing, requiredTruckCount, detail]);

  useEffect(() => {
    if (detail) {
      setRequiredTruckCount(detail.requiredTruckCount);
      setBerthReservations(
        (detail.laneBerthReservations || []).map((r) => ({
          ...r,
          tempId: `berth-${r.id}`,
        })),
      );
      setManualAssignments(
        (detail.laneManualAssignments || []).map((a) => ({
          ...a,
          tempId: `assignment-${a.id}`,
        })),
      );
    }
  }, [detail]);

  useEffect(() => {
    if (!detail || isEditing) return;
    resetBasicInfoDraftFromDetail();
    setBasicInfoDirty(false);
  }, [detail, isEditing, resetBasicInfoDraftFromDetail]);

  useEffect(() => {
    if (!detail) return;
    if (requiredTruckCount === null || requiredTruckCount <= 0) {
      return;
    }
    if (manualAssignments.length > requiredTruckCount) {
      setManualAssignments((prev) => prev.slice(0, requiredTruckCount));
      return;
    }
    const baseQuantity = Math.floor(detail.volumeSummary.packages / requiredTruckCount);
    const baseWeight = Math.floor(detail.volumeSummary.weightKg / requiredTruckCount);
    const baseVolume = Math.floor(detail.volumeSummary.volumeM3 / requiredTruckCount);

    const reminderQuantity = detail.volumeSummary.packages % baseQuantity;
    const reminderWeight = detail.volumeSummary.weightKg % baseWeight;
    const reminderVolume = detail.volumeSummary.volumeM3 % baseVolume;

    for (let i = manualAssignments.length; i < requiredTruckCount; i++) {
      setManualAssignments((prev) =>
        [
          ...prev,
          { tempId: `assignment-${Date.now()}` } as EditableAssignment,
        ].map((assignment, index) => ({
          ...assignment,
          quantity: baseQuantity + (index === prev.length && reminderQuantity > 0 ? reminderQuantity : 0),
          weightKg: baseWeight + (index === prev.length && reminderWeight > 0 ? reminderWeight : 0),
          volumeM3: baseVolume + (index === prev.length && reminderVolume > 0 ? reminderVolume : 0),
        })),
      );
    }
  }, [detail, manualAssignments.length, requiredTruckCount]);

  const markBasicInfoDirty = useCallback(() => {
    setBasicInfoDirty(true);
  }, []);

  const startEditing = useCallback(() => {
    resetBasicInfoDraftFromDetail();
    setBasicInfoDirty(false);
    setIsEditing(true);
  }, [resetBasicInfoDraftFromDetail]);

  const cancelEditing = useCallback(() => {
    if (detail) {
      setRequiredTruckCount(detail.requiredTruckCount);
      setBerthReservations(
        (detail.laneBerthReservations || []).map((r) => ({
          ...r,
          tempId: `berth-${r.id}`,
        })),
      );
      setManualAssignments(
        (detail.laneManualAssignments || []).map((a) => ({
          ...a,
          tempId: `assignment-${a.id}`,
        })),
      );
    }
    resetBasicInfoDraftFromDetail();
    setBasicInfoDirty(false);
    setModified(false);
    setIsEditing(false);
  }, [detail, resetBasicInfoDraftFromDetail]);

  const saveChanges = useCallback(async () => {
    if (!detail) return;

    if (basicInfoDirty && basicInfoDraft) {
      if (!validateLaneBasicInfoDraft(detail.shipmentOrders, basicInfoDraft)) {
        enqueueSnackbar(LOADING_UNLOADING_DATE_RANGE_ERROR_MESSAGE, { variant: 'error' });
        return;
      }
    }

    const isUnassigned = detail.laneStatus === 'not_planned'
      && (detail.requiredTruckCount === null || detail.requiredTruckCount === 0);
    const effectiveRequiredTruckCount = isUnassigned && manualAssignments.length > 0
      ? 1
      : requiredTruckCount;

    const request: UpdateShipmentOrderLaneRequest = {
      id: detail.id,
      requiredTruckCount: effectiveRequiredTruckCount,
      berthReservations: berthReservations.map(({ tempId: _, ...rest }) => rest),
      manualAssignments: manualAssignments.map(({ tempId: _, ...rest }) => ({
        ...rest,
        quantity: rest.quantity ?? (isUnassigned ? detail.volumeSummary.packages : null),
        weightKg: rest.weightKg ?? (isUnassigned ? detail.volumeSummary.weightKg : null),
        volumeM3: rest.volumeM3 ?? (isUnassigned ? detail.volumeSummary.volumeM3 : null),
      })),
    };

    setIsSaving(true);
    try {
      await updateLaneMutation.mutateAsync(request);

      if (basicInfoDirty && basicInfoDraft && detail.shipmentOrders.length > 0) {
        for (const order of detail.shipmentOrders) {
          const payload = buildShipmentDetailFormDataForLaneSave(order, basicInfoDraft);
          await updateShipmentOrder.mutateAsync(payload);
        }
      }

      await queryClient.invalidateQueries({
        queryKey: [SHIPMENT_ORDER_LANE_DETAIL_QUERY_KEY, detail.id],
      });

      enqueueSnackbar('レーン情報を更新しました', { variant: 'success' });
      setIsEditing(false);
      setModified(false);
      setBasicInfoDirty(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : '不明なエラー';
      enqueueSnackbar(`更新に失敗しました: ${message}`, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [
    detail,
    basicInfoDirty,
    basicInfoDraft,
    requiredTruckCount,
    berthReservations,
    manualAssignments,
    updateLaneMutation,
    updateShipmentOrder,
    queryClient,
    enqueueSnackbar,
  ]);

  const updateBerthReservation = useCallback(
    (tempId: string, field: keyof Omit<LaneBerthReservation, 'id'>, value: string | null) => {
      setModified(true);
      let processedValue = value;
      if ((field === 'reservedFrom' || field === 'reservedTo') && value && detail?.loadingOn) {
        const dateTime = datetimeDecorator.fromHourMinutesToDate(value, detail.loadingOn);
        processedValue = dateTime ? dateTime.toISOString() : value;
      }
      setBerthReservations((prev) =>
        prev.map((r) => (r.tempId === tempId ? { ...r, [field]: processedValue } : r)),
      );
    },
    [detail?.loadingOn],
  );

  const addBerthReservation = useCallback((kind: 'loading' | 'unloading') => {
    const newReservation: EditableBerthReservation = {
      id: undefined,
      tempId: `new-${Date.now()}`,
      kind,
      reservationNumber: null,
      reservedFrom: null,
      reservedTo: null,
      carrierCompany: null,
      truckNumber: null,
      driverName: null,
      driverContact: null,
    };
    setBerthReservations((prev) => [...prev, newReservation]);
    setModified(true);
  }, []);

  const removeBerthReservation = useCallback((tempId: string) => {
    setBerthReservations((prev) => prev.filter((r) => r.tempId !== tempId));
    setModified(true);
  }, []);

  const updateManualAssignment = useCallback(
    (tempId: string, field: keyof Omit<LaneManualAssignment, 'id'>, value: string | number | null) => {
      setManualAssignments((prev) =>
        prev.map((a) => (a.tempId === tempId ? { ...a, [field]: value } : a)),
      );
      setModified(true);
    },
    [],
  );

  const addManualAssignment = useCallback(() => {
    const newAssignment: EditableAssignment = {
      id: undefined,
      tempId: `new-${Date.now()}`,
      sequenceNumber: manualAssignments.length + 1,
      quantity: null,
      weightG: null,
      weightKg: null,
      volumeMm3: null,
      volumeM3: null,
      carrierCompany: null,
      operatingCarrier: null,
      driverName: null,
      truckNumber: null,
      hierarchyLevel: null,
    };
    setManualAssignments((prev) => [...prev, newAssignment]);
    setModified(true);
  }, [manualAssignments.length]);

  const removeManualAssignment = useCallback((tempId: string) => {
    setManualAssignments((prev) => {
      const filtered = prev.filter((a) => a.tempId !== tempId);
      return filtered.map((a, index) => ({
        ...a,
        sequenceNumber: index + 1,
      }));
    });
    setModified(true);
  }, []);

  const combinedModified = modified || basicInfoDirty;

  return {
    isEditing,
    isSaving,
    requiredTruckCount,
    berthReservations,
    manualAssignments,
    modified: combinedModified,
    basicInfoDraft,
    setBasicInfoDraft,
    markBasicInfoDirty,
    startEditing,
    cancelEditing,
    saveChanges,
    setRequiredTruckCount,
    updateBerthReservation,
    addBerthReservation,
    removeBerthReservation,
    updateManualAssignment,
    addManualAssignment,
    removeManualAssignment,
  };
};
